import { z } from 'zod';
import { IsoDateTime, UuidString } from '../../primitives.js';

/**
 * Notification — уведомление task-module (BE-S14-1).
 *
 * Structural-only (i18n invariant): FE рендерит текст по trigger + refs.
 */

export const NotificationChannel = z.enum(['in_app', 'email', 'bitrix']);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const NotificationTrigger = z.enum([
  'assigned_to_me',
  'mention',
  'comment_on_watched',
  'state_changed_watched',
  'due_soon',
  'overdue',
  'dependency_unblocked',
  'claim_available',
  /** B11 (sla-lifecycle §3.2) — свод «в проекте N просрочек» вместо залпа алармов.
   *  Собственного тумблера в матрице НЕ имеет: читает настройки overdue/due_soon. */
  'sla_digest',
  /**
   * B12 (project-plan-change §D) — проект отменён, задачи исполнителя закрыты.
   *
   * Замер после отмены `PROJ-2026-9014` (`cancel_count = 3`): 0 новых строк за
   * пять минут. Риск «залпа» не подтвердился, но обратная сторона оказалась хуже
   * залпа — исполнителю НИКТО не сообщал, что его задачу закрыли.
   *
   * Коалесценция по проекту, а не по задаче: адресат получает ОДНУ строку с
   * числом закрытых задач, сколько бы их ни было. `task_id` всегда NULL — как у
   * `sla_digest`, по той же причине: предмет — проект.
   */
  'project_cancelled',
  /**
   * B8 (tier2-approval-requests §7.2-О6) — заявка на согласование.
   *
   * Адресная заявка без уведомления адресата — это заявка, о которой никто не узнал.
   * Канал расширен, а не заведён второй: у уведомлений уже есть настройки, дедуп по
   * суткам и коалесценция, и второй механизм пришлось бы учить тому же заново.
   *
   * Предмет — проект (`task_id` всегда NULL), коалесценция по проекту: пять заявок
   * подряд одному адресату дают одну строку со счётчиком, а не пять писем в минуту.
   */
  'approval_requested',
  /** Решение по заявке — инициатору: согласовано, отклонено или не исполнилось. */
  'approval_decided',
  /** Заявка истекла, не дождавшись решения, — инициатору. */
  'approval_expired',
  /**
   * B39 пака `tails-closeout` (T2A-F14) — снимок последствий заявки пересобран, инициатору.
   * Отдельный триггер, а не `approval_decided`: заявка НЕ решена, решать по ней снова
   * можно, и строка «решено» соврала бы о состоянии.
   */
  'approval_impact_rebuilt',
  /**
   * B8 (work-tasks-user-feedback §3.3) — этап отменён каскадом.
   *
   * Одна строка на наблюдателя папки со счётчиком отменённых задач, а не залп
   * per-task [Б14]: каскад по этапу на полсотни задач иначе выдал бы полсотни
   * строк одному человеку за одну секунду.
   *
   * Предмет — ПАПКА, поэтому `task_id` заполнен (в отличие от `project_cancelled`,
   * где предмет проект): человеку нужно попасть именно в отменённый этап.
   * Коалесценция — по паре (user, task) существующим partial-unique.
   *
   * Письма у триггера нет осознанно: он не входит в `EMAIL_CAPABLE_TRIGGERS`
   * (B17), и email-тумблер уходит в disabled-путь (пара F13/F23).
   */
  'subtree_cancelled',
  /**
   * B21 (work-tasks-user-feedback §3.8) — таймер бежит слишком долго.
   *
   * Забытый на ночь таймер при стопе запишет в журнал фиктивные часы, и увидит
   * это только тот, кто станет разбираться в себестоимости через месяц. Пока
   * вкладка закрыта, о бегущем таймере не знает никто — индикатор в шапке живёт
   * ровно столько, сколько открыт браузер.
   *
   * Предмет — ЗАДАЧА (`task_id` заполнен): человеку нужно попасть в её секцию
   * «Время» и остановить. Повтор — не чаще суток (`last_reminded_at` в самой
   * строке таймера, не в уведомлении: дедуп относится к таймеру, а не к записи).
   *
   * Письма нет осознанно: триггер не входит в `EMAIL_CAPABLE_TRIGGERS`, и
   * email-тумблер уходит в disabled-путь (пара F31, паттерн F23). Напоминание о
   * бегущем таймере имеет смысл там, где его можно остановить, — в приложении.
   */
  'timer_reminder',
  /**
   * WTU-F1 (свип tails-sweep-2) — человек попросил доступ.
   *
   * Адресат — администратор компании: отказ, который человек реально видит на
   * экране (403), это отсутствие МОДУЛЯ, а модульные гранты выдаёт он. Лид
   * проекта адресатом быть не может, не отменив соседний инвариант: закрытый
   * проект отдаёт не-участнику 404, и «просьба лиду проекта X» подтверждала бы
   * существование X.
   *
   * Предмет — задача, если она известна (`task_id` заполнен: админу нужно
   * увидеть, куда человек шёл). Коалесценция по паре (user, task) существующим
   * partial-unique; заявка и без того одна открытая на просителя в компании.
   */
  'access_requested',
  /** Доступ выдан — просителю. Замыкает круг: он узнаёт, что можно вернуться. */
  'access_granted',
  /**
   * chat-interactive-actions B14 (§2.6) — массовый комментарий: одна сводка
   * «прокомментировано N задач» вместо залпа `comment_on_watched` per-task.
   *
   * Наблюдатель двадцати задач при комментарии к пачке получил бы двадцать строк
   * за секунду — это не уведомление, а способ приучить человека их не читать.
   *
   * Предмет — ПАЧКА, поэтому `task_id` всегда NULL; `project_id` заполнен, если
   * вся пачка в одном проекте (тогда работает коалесценция по проекту), и NULL для
   * кросс-проектной. Собственного тумблера в матрице НЕ имеет: читает настройки
   * `comment_on_watched` — по природе это тот же комментарий к отслеживаемой задаче.
   */
  'bulk_comment_digest',
]);
export type NotificationTrigger = z.infer<typeof NotificationTrigger>;

/**
 * B17 (work-tasks-user-feedback §3.5) — триггеры, у которых ЕСТЬ письмо.
 *
 * До этого множество существовало только внутри `switch` в шаблонах, и
 * расхождение было молчаливым в обе стороны: у `comment_on_watched` тумблер
 * «письмо» включался, шаблон возвращал `null`, и письмо не уходило — пользователь
 * считал, что подписан [UF-11]. Обратный случай не лучше: новый шаблон без
 * тумблера — письмо, которое нельзя выключить.
 *
 * Список — контракт для FE (F23 гасит чекбокс у триггеров вне списка) и для
 * зеркального теста шаблонов: множество веток `switch` обязано совпасть с этим
 * набором.
 */
export const EMAIL_CAPABLE_TRIGGERS = [
  'assigned_to_me',
  'mention',
  'comment_on_watched',
  'due_soon',
  'overdue',
  /**
   * WTU-BE-2 (свип tails-sweep-2) — забытый таймер обязан догонять человека
   * ПОЧТОЙ, а не только карточкой в центре.
   *
   * Диагноз книги: таймер, запущенный в компании А, невидим, пока выбрана
   * компания Б, — и при стопе запишет часы, о которых человек не помнит. Джоба
   * напоминания системная и таймеры во всех компаниях видит; но уведомление она
   * создаёт В КОМПАНИИ ТАЙМЕРА, а центр уведомлений скоупится активной компанией
   * (и под RLS иначе не может). То есть до этой правки напоминание было ровно так
   * же заперто в компании А, как и индикатор, который оно должно было заменить.
   *
   * Почта скоупа не имеет вовсе: письмо доходит независимо от того, какая
   * компания выбрана в браузере и открыт ли браузер. Это и есть «напоминание, не
   * зависящее от активной компании» из тела задачи — минимальный шаг, не
   * требующий поверхности «мои таймеры по всем компаниям» (она остаётся эпиком).
   */
  'timer_reminder',
] as const satisfies readonly NotificationTrigger[];

export type EmailCapableTrigger = (typeof EMAIL_CAPABLE_TRIGGERS)[number];

export function isEmailCapableTrigger(trigger: string): trigger is EmailCapableTrigger {
  return (EMAIL_CAPABLE_TRIGGERS as readonly string[]).includes(trigger);
}

/**
 * B1/B2 (sla-lifecycle §1.2) — почему эпизод перестал быть правдой.
 * Enum-код (i18n-инвариант: локализует FE, backend freeform-строк не шлёт).
 */
export const NotificationResolveReason = z.enum([
  /** Задача завершена. */
  'task_completed',
  /** Задача архивирована (в т.ч. каскадом от проекта). */
  'task_archived',
  /**
   * B3 (project-actions-parity §2.2) — задачу закрыли как отменённую вместе с
   * проектом. Отличается от `project_inactive` адресатом: там причина в проекте,
   * здесь исход получила сама задача, и «работа не будет сделана» — про неё.
   */
  'task_cancelled',
  /** Проект вышел из SLA-скоупа (§2.3): архив, on_hold, completed, PM-planning. */
  'project_inactive',
  /** Срок задачи изменился — снапшот эпизода больше не соответствует правде. */
  'due_date_changed',
  /** Вытеснено более сильным эпизодом: due_soon → наступила просрочка (§1.5). */
  'superseded',
  /** B15 (§9.4) — пользователь сузил окно `due_soon_lead_days`, строка вне окна. */
  'prefs_changed',
  /**
   * B13 (прод-замер 22.07): задачи, на которую ссылается строка, в БД больше нет
   * (жёсткое удаление до перехода на soft-archive). Правила a–e её не видят —
   * они идут через JOIN. 60 таких фантомов висело в проде живыми.
   */
  'task_deleted',
  /** С-G Р.6 — заявка на подпись решена, отозвана, переадресована или истекла. */
  'approval_closed',
]);
export type NotificationResolveReason = z.infer<typeof NotificationResolveReason>;

/**
 * Структурные параметры уведомления (notification-center §2.3). Дискриминатор —
 * СИБЛИНГ-поле `trigger` (не внутри params), поэтому это не discriminatedUnion, а
 * гибкая структура с типизированными optional-полями. `count` добавляется
 * коалесценцией (B2) на любой триггер; FE читает поля по trigger. i18n-structural:
 * только числа/даты/UUID-refs, никакого freeform-текста.
 */
export const NotificationParams = z
  .object({
    /** Коалесценция B2: сколько событий свернулось в запись (≥1). */
    count: z.number().int().positive().optional(),
    /** overdue / due_soon. */
    due_date: z.string().nullable().optional(),
    /** due_soon: сколько дней до срока (порог — per-user lead, B15). */
    days_left: z.number().int().optional(),
    /** overdue: на сколько дней просрочено НА МОМЕНТ уведомления (снапшот эпизода, И-3). */
    days_overdue: z.number().int().optional(),
    /** overdue: стадия эпизода — первый аларм / единственный повтор через 3 дня (§2.5). */
    stage: z.enum(['initial', 'reminder_3d']).optional(),
    /** mention: комментарий-источник. */
    comment_id: UuidString.optional(),
    /** mention/comment_on_watched: цитата комментария (первые 200 симв., B12 раунд 2).
     *  Optional — старые записи без цитаты валидны (backfill не делаем, §6.5). */
    comment_snippet: z.string().optional(),
    /** state_changed_watched. */
    from_group: z.string().optional(),
    to_group: z.string().optional(),
    // ── sla_digest (B11 §3.2) — свод по проекту за прогон ──
    /** Сколько просрочек свернулось в свод. */
    overdue_count: z.number().int().optional(),
    /** Сколько «срок приближается» свернулось в свод. */
    due_soon_count: z.number().int().optional(),
    /** Первые несколько задач свода (для превью, без текстов). */
    sample_task_ids: z.array(UuidString).optional(),
    /** Дата прогона, к которой относится снимок (`YYYY-MM-DD`). */
    on_date: z.string().optional(),
    /** timer_reminder (B21) — сколько ЦЕЛЫХ часов таймер бежит непрерывно. */
    running_hours: z.number().int().positive().optional(),
    /** bulk_comment_digest (B14) — сколько отслеживаемых задач получили комментарий. */
    task_count: z.number().int().positive().optional(),
  })
  .catchall(z.unknown());
export type NotificationParams = z.infer<typeof NotificationParams>;

export const Notification = z.object({
  id: UuidString,
  entity_id: UuidString,
  user_id: UuidString,
  channel: NotificationChannel,
  trigger: NotificationTrigger,
  task_id: UuidString.nullable(),
  project_id: UuidString.nullable(),
  actor_user_id: UuidString.nullable(),
  params: NotificationParams,
  read_at: IsoDateTime.nullable(),
  delivered_at: IsoDateTime.nullable(),
  /** notification-center B1 — email-дедуп (когда слали письмо по записи). */
  last_emailed_at: IsoDateTime.nullable(),
  /** B1 (sla-lifecycle §1.2) — эпизод резолвнут (перестал быть правдой); NULL = живой. */
  resolved_at: IsoDateTime.nullable(),
  /** B1 — структурная причина резолва; NULL пока эпизод жив. */
  resolve_reason: NotificationResolveReason.nullable(),
  /**
   * B12 (work-tasks-user-feedback §3.5) — человек убрал строку с глаз.
   *
   * Nullable-**optional**: старая вкладка в окне выката поля не получает и не
   * падает (§6.2). Ось третья и ортогональная: `read_at` — «я это видел»,
   * `resolved_at` — «факт мира изменился», эта — «мне это больше не нужно».
   */
  dismissed_at: IsoDateTime.nullable().optional(),
  created_at: IsoDateTime,
  // ── Денорм (notification-center B3 §2.3, инвариант I1) ──────────────
  // Все `*|null`: недоступная (RLS/архив чужого поддерева) или удалённая
  // задача отдаёт null — FE показывает «Задача недоступна» без deep-link,
  // не глотая строку.
  /** Заголовок задачи или null (недоступна). */
  task_title: z.string().nullable(),
  /** sequence_id задачи (для `#seq`). */
  task_seq: z.number().int().nullable(),
  /** Имя проекта задачи (от task.project_id, НЕ от протухающего notifications.project_id). */
  project_name: z.string().nullable(),
  /** Код проекта (для `<код>-<seq>`). */
  project_code: z.string().nullable(),
  /** id проекта задачи (task.project_id) — для фильтра центра по проекту (F12, B11). */
  task_project_id: UuidString.nullable(),
  /**
   * B10a (sla-lifecycle §4.2, инвариант И-3) — ТЕКУЩИЙ срок задачи, `YYYY-MM-DD`.
   * Без него список показывал только замороженный `params.due_date`: у резолвнутой
   * строки это единственное число на экране, и оно устаревшее. Пара «было → стало»
   * в одной мета-строке — то, ради чего И-3 и вводился.
   */
  task_due_date: z.string().nullable(),
  /** Имя актора события. */
  actor_name: z.string().nullable(),
});
export type Notification = z.infer<typeof Notification>;

/** Настройка: per (channel × trigger) включено/выключено. */
export const NotificationSetting = z.object({
  user_id: UuidString,
  channel: NotificationChannel,
  trigger: NotificationTrigger,
  enabled: z.boolean(),
});
export type NotificationSetting = z.infer<typeof NotificationSetting>;

/**
 * B14 (sla-lifecycle §9.3) — параметры триггеров, единые для BE и FE.
 * ЕДИНСТВЕННОЕ определение формы prefs: FE не дублирует инлайн-Zod `[S-C4-11]`.
 */
export const NotificationPrefs = z
  .object({
    /** За сколько дней до срока слать `due_soon`. Отсутствующая строка = 3. */
    due_soon_lead_days: z.number().int().min(1).max(14),
  })
  // Dual-use: та же схема валидирует ТЕЛО PUT /settings, где лишний ключ обязан
  // падать 400 — иначе опечатка в имени параметра теряется молча.
  // zod-strict-ok: input-схема, а не response
  .strict();
export type NotificationPrefs = z.infer<typeof NotificationPrefs>;

/** B14 — ответ `GET /task-module/notifications/settings`: матрица + параметры. */
export const NotificationSettingsResponse = z
  .object({
    settings: z.array(
      z
        .object({
          channel: NotificationChannel,
          trigger: NotificationTrigger,
          enabled: z.boolean(),
        })
        // zod-strict-ok: элемент тела PUT /settings
        .strict(),
    ),
    prefs: NotificationPrefs,
  })
  // Input-эхо: форма совпадает с телом PUT, лишние ключи ловим на границе —
  // ответ читает ровно та вкладка, что его и отправляет.
  // zod-strict-ok: input-эхо PUT /settings
  .strict();
export type NotificationSettingsResponse = z.infer<typeof NotificationSettingsResponse>;
